import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { priceSubmissionService } from '../services/api';

interface ReportPriceModalProps {
  cardId: number;
  cardName: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ReportPriceModal = ({ cardId, cardName, isOpen, onClose, onSuccess }: ReportPriceModalProps) => {
  const { t } = useTranslation();
  // Helper to get today's date in YYYY-MM-DD format (Local Time)
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [submittedPrice, setSubmittedPrice] = useState('');
  const [priceDate, setPriceDate] = useState(getTodayDate());
  // Default source to 'Community Report' as UI section is removed
  const [source, setSource] = useState('Community Report');
  const [sourceUrl, setSourceUrl] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [cardSequence, setCardSequence] = useState(1);
  const [notes, setNotes] = useState('');
  const [proofImage, setProofImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Handle preview URL creation and cleanup
  useEffect(() => {
    if (!proofImage) {
      setPreviewUrl('');
      return;
    }

    const objectUrl = URL.createObjectURL(proofImage);
    console.log('Created preview URL:', objectUrl, 'for file:', proofImage.name);
    setPreviewUrl(objectUrl);

    // Cleanup function
    return () => {
      console.log('Revoking preview URL:', objectUrl);
      URL.revokeObjectURL(objectUrl);
    };
  }, [proofImage]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate inputs
    const price = parseFloat(submittedPrice);
    if (isNaN(price) || price <= 0) {
      setError(t('reportPrice.errors.invalidPrice'));
      return;
    }

    if (!sourceUrl.trim()) {
      setError(t('reportPrice.errors.missingUrl'));
      return;
    }

    // Proof image is now optional for moderators
    // if (!proofImage) {
    //   setError(t('reportPrice.errors.missingProofImage') || 'Proof image is required');
    //   return;
    // }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('cardId', cardId.toString());
      formData.append('submittedPrice', price.toFixed(2));
      formData.append('priceDate', priceDate);
      formData.append('source', source.trim());
      formData.append('quantity', quantity.toString());
      formData.append('cardSequence', cardSequence.toString());
      if (sourceUrl.trim()) formData.append('sourceUrl', sourceUrl.trim());
      if (notes.trim()) formData.append('notes', notes.trim());
      if (proofImage) formData.append('proofImage', proofImage);

      await priceSubmissionService.submitPrice(formData);

      // Reset form
      setSubmittedPrice('');
      setPriceDate(getTodayDate());
      setSource('Community Report'); // Reset to default
      setSourceUrl('');
      setQuantity(1);
      setCardSequence(1);
      setNotes('');
      setProofImage(null);

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      // Close modal
      onClose();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message;
      if (errorMessage === 'DUPLICATE_SOURCE_URL') {
        setError(t('reportPrice.errors.duplicateUrl'));
      } else if (errorMessage?.startsWith('DUPLICATE: Submission for card sequence')) {
        const match = errorMessage.match(/sequence (\d+)/);
        const sequence = match ? match[1] : '';
        setError(t('reportPrice.errors.duplicateSequence', { sequence }));
      } else if (errorMessage?.startsWith('Invalid Sequence:')) {
        const match = errorMessage.match(/card #(\d+) before card #(\d+)/);
        const prev = match ? match[1] : '';
        const current = match ? match[2] : '';
        setError(t('reportPrice.errors.invalidSequence', { prev, current }));
      } else {
        setError(errorMessage || t('reportPrice.errors.submitFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setError('');
      onClose();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (e.clipboardData.files.length > 0) {
      const file = e.clipboardData.files[0];
      if (file.type.startsWith('image/')) {
        setProofImage(file);
      }
    }
  };

  const handlePasteClick = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        const imageType = item.types.find(type => type.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          const file = new File([blob], "pasted-image.png", { type: blob.type });
          setProofImage(file);
          return;
        }
      }
      // If we got here, no image was found
      alert(t('reportPrice.errors.noImageInClipboard') || 'No image found in clipboard');
    } catch (err) {
      console.error('Failed to read clipboard:', err);
      alert(t('reportPrice.errors.clipboardAccessDenied') || 'Could not access clipboard. Please click inside the box and use Ctrl+V / Cmd+V to paste.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">{t('reportPrice.title')}</h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6" onPaste={handlePaste}>
          {/* Hidden File Input - Kept mounted to preserve file reference */}
          <input
            id="proofImage"
            name="proofImage"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              console.log('File input changed:', file);
              if (file) setProofImage(file);
            }}
            disabled={isSubmitting}
          />

          {/* Card Name */}
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              {t('reportPrice.submittingFor')} <span className="font-semibold text-gray-800">{cardName}</span>
            </p>
          </div>

          {/* Price Input */}
          <div className="mb-4">
            <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
              {t('reportPrice.priceLabel')}
            </label>
            <input
              type="number"
              id="price"
              step="0.01"
              min="0"
              value={submittedPrice}
              onChange={(e) => setSubmittedPrice(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('reportPrice.pricePlaceholder')}
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Price Date Input */}
          <div className="mb-4">
            <label htmlFor="priceDate" className="block text-sm font-medium text-gray-700 mb-2">
              {t('reportPrice.dateLabel')}
            </label>
            <input
              type="date"
              id="priceDate"
              value={priceDate}
              onChange={(e) => setPriceDate(e.target.value)}
              max={getTodayDate()}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              disabled={isSubmitting}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('reportPrice.dateHelp')}
            </p>
          </div>


          {/* Source URL Input */}
          <div className="mb-4">
            <label htmlFor="sourceUrl" className="block text-sm font-medium text-gray-700 mb-2">
              {t('reportPrice.urlLabel')}
            </label>
            <input
              type="url"
              id="sourceUrl"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('reportPrice.urlPlaceholder')}
              required
              disabled={isSubmitting}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('reportPrice.urlHelp')}
            </p>

          </div>

          {/* Card Sequence Input */}
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
                {t('reportPrice.quantityLabel')}
              </label>
              <select
                id="quantity"
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isSubmitting}
              >
                {[...Array(10)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {t('reportPrice.quantityHelp')}
              </p>
            </div>

            <div className="flex-1">
              <label htmlFor="cardSequence" className="block text-sm font-medium text-gray-700 mb-2">
                {t('reportPrice.cardSequenceLabel')}
              </label>
              <select
                id="cardSequence"
                value={cardSequence}
                onChange={(e) => setCardSequence(Number(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isSubmitting}
              >
                {[...Array(20)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {t('reportPrice.cardSequenceHelp')}
              </p>
            </div>
          </div>

          {/* Notes Input */}
          <div className="mb-4">
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
              {t('reportPrice.notesLabel')}
            </label>
            <textarea
              id="notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder={t('reportPrice.notesPlaceholder')}
              disabled={isSubmitting}
            />
          </div>

          {/* Proof Image Input - HIDDEN FOR NOW */}
          <div className="mb-4 hidden">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('reportPrice.proofImageLabel')}
            </label>

            {proofImage ? (
              <div className="mb-3 relative group w-fit">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="h-32 rounded border border-gray-200"
                    onError={(e) => console.error('Preview image failed to load', e)}
                    onLoad={() => console.log('Preview image loaded successfully')}
                  />
                ) : (
                  <div className="h-32 w-32 rounded border border-gray-200 bg-gray-100 flex items-center justify-center">
                    <span className="text-gray-400 text-sm">Loading...</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setProofImage(null);
                    // Reset file input value
                    const fileInput = document.getElementById('proofImage') as HTMLInputElement;
                    if (fileInput) fileInput.value = '';
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            ) : (
              <div
                className="mt-1 flex flex-col justify-center items-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:bg-gray-50 transition cursor-pointer"
                onDrop={(e) => {
                  e.preventDefault();
                  console.log('File dropped');
                  if (e.dataTransfer.files?.[0]) {
                    setProofImage(e.dataTransfer.files[0]);
                  }
                }}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => {
                  // Manually trigger the hidden input if needed, but LABEL should handle it
                  // However, since we might click outside the label text but inside the box...
                  // Better to let the box BE the label or wrap content in label
                }}
              >
                <label htmlFor="proofImage" className="w-full cursor-pointer flex flex-col items-center">
                  <div className="space-y-1 text-center pointer-events-none">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className="flex text-sm text-gray-600 justify-center">
                      <span className="relative rounded-md font-medium text-blue-600 hover:text-blue-500">
                        <span>Upload a file</span>
                      </span>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      {t('reportPrice.fileSizeLimit')}
                    </p>
                    <p className="text-sm font-semibold text-blue-500 pt-2">
                      or Paste Code/Image (Ctrl+V)
                    </p>
                  </div>
                </label>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePasteClick();
                  }}
                  className="mt-2 pointer-events-auto inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="-ml-0.5 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  Paste from Clipboard
                </button>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {t('reportPrice.proofImageHelp')}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Info Message */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-600">
              {t('reportPrice.earningInfo')}
              <span className="font-semibold">{t('reportPrice.earnPoints')}</span>
              <br />
              <span className="text-xs mt-1 block">
                {(() => {
                  const userStr = localStorage.getItem('user');
                  if (userStr) {
                    const user = JSON.parse(userStr);
                    const isPrivileged = user.role === 'admin' || user.role === 'moderator' || user.role === 'advanced_user';
                    return isPrivileged ? t('reportPrice.immediateUpdate') : t('reportPrice.pendingApproval');
                  }
                  return t('reportPrice.pendingApproval');
                })()}
              </span>
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('reportPrice.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('reportPrice.submitting') : t('reportPrice.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportPriceModal;
